import { transformEnumToDisplay } from '@fruitful/fruitils';
import {
    type DepositAccount,
    type ImAccount,
    isRetirementRegistrationType,
    RetirementContributionType,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import { isAfter } from 'date-fns';
import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useMemo, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteRegular,
    FullScreenSpinner,
    ScreenContainer,
    SelectDropdown,
    YStack,
} from 'smoothie';

import { INVESTMENT_NOT_FDIC_INSURED } from '../../../banking/components/Disclosures';
import SelectAccountSheet from '../../../banking/components/transfers/SelectAccountSheet';
import TransferAccountLineItem from '../../../banking/components/transfers/TransferAccountLineItem';
import useGetInternalDepositAccounts from '../../../banking/hooks/useGetInternalDepositAccounts';
import { assertNotNullish } from '../../../common/misc';
import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useGetApexAccounts } from '../../hooks/useGetApexAccounts';
import {
    apexDepositRequestAtom,
    selectedApexTransferAccount,
} from '../../state/investmentAtoms';
import { ApexAccountLineItem } from './ApexAccountLineItem';
import {
    BackdoorRothIctDepositWarningNotice,
    shouldWarnBackdoorRothIctDeposit,
} from './BackdoorRothIctDepositWarningNotice';
import {
    BackdoorTradIctDepositConversionNotice,
    shouldShowBackdoorTradConversionNotice,
} from './BackdoorTradIctDepositConversionNotice';
import SelectApexAccountSheet from './SelectApexAccountSheet';

export const ApexDepositForm = () => {
    useTrackViewOnFocus('Investment/Transfer/Deposit');

    const currentYear = new Date().getFullYear();
    const currentYearString = currentYear.toString(10);

    const preselectedApexTransferAccount = useAtomValue(
        selectedApexTransferAccount,
    );
    const setApexDepositRequest = useSetAtom(apexDepositRequestAtom);

    const [isSelectFromAccountSheetOpen, setIsSelectFromAccountSheetOpen] =
        useState(false);
    const [isSelectApexAccountSheetOpen, setIsSelectApexAccountSheetOpen] =
        useState(false);

    const [selectedDepositAccount, setSelectedDepositAccount] =
        useState<DepositAccount | null>(null);

    const [selectedApexAccount, setSelectedApexAccount] =
        useState<ImAccount | null>(preselectedApexTransferAccount);

    const [contributionYear, setContributionYear] =
        useState<string>(currentYearString);

    const isSelectedSepIra =
        selectedApexAccount?.registrationType ===
        SupportedApexAccountRegistrationType.RegistrationIraSep;

    const [contributionType, setContributionType] = useState<string>(
        transformEnumToDisplay(
            isSelectedSepIra
                ? RetirementContributionType.EMPLOYER
                : RetirementContributionType.REGULAR,
        ),
    );

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalDepositAccounts({});

    const { data: apexAccounts, isLoading: isLoadingApexAccounts } =
        useGetApexAccounts();

    const isRetirementAccount = useMemo(
        () =>
            isRetirementRegistrationType(selectedApexAccount?.registrationType),
        [selectedApexAccount?.registrationType],
    );

    const contributionTypes = [transformEnumToDisplay(contributionType)];

    // Not all contribution types allow prior year contributions
    const contributionYears = useMemo(() => {
        const contributionContraint =
            selectedApexAccount?.contributionConstraints?.validTypes?.find(
                ({ type }) => {
                    return (
                        transformEnumToDisplay(assertNotNullish(type)) ===
                        contributionType
                    );
                },
            );

        const showPreviousYear =
            contributionContraint?.previousYearAllowed ?? false;

        // Only show prior year if the current date is not after the cutoff date,
        // which is not always tax day.
        const isAfterCutoff = () => {
            const deadline = contributionContraint?.previousYearDeadline;
            if (deadline != null) {
                const { year, month, day } = deadline;
                const cutoff = new Date(year, month - 1, day);
                // This is probably sketchy with timezones and stuff
                return isAfter(new Date(), cutoff);
            }

            return true; // Hide the contribution year dropdown
        };

        const years = [currentYear];
        if (showPreviousYear && !isAfterCutoff()) {
            years.push(currentYear - 1);
        }
        return years.map((y) => y.toString(10));
    }, [selectedApexAccount, contributionType, isRetirementAccount]);

    const isValid = useMemo(() => {
        if (selectedApexAccount == null) {
            return false;
        }
        if (selectedDepositAccount == null) {
            return false;
        }
        if ((selectedDepositAccount.availableBalance ?? 0) <= 0) {
            return false;
        }
        if (contributionType == null) {
            return false;
        }
        return !(
            selectedApexAccount.isRetirementAccount && contributionYear == null
        );
    }, [selectedApexAccount, selectedDepositAccount]);

    // TODO write a <Select> component that can deal with enums
    // Turn the string back into the enum it was originally
    const contributionTypeEnum = useMemo(() => {
        return (
            selectedApexAccount?.contributionConstraints?.validTypes?.find(
                ({ type }) => {
                    return (
                        transformEnumToDisplay(assertNotNullish(type)) ===
                        contributionType
                    );
                },
            )?.type ?? undefined
        );
    }, [contributionType, selectedApexAccount]);

    const depositRequest =
        selectedDepositAccount != null && selectedApexAccount != null
            ? {
                  toAccount: selectedApexAccount,
                  fromAccount: selectedDepositAccount,
                  contributionType: contributionTypeEnum,
                  contributionYear: parseInt(contributionYear, 10),
                  isRetirementAccount,
              }
            : null;

    if (isLoadingDepositAccounts || isLoadingApexAccounts) {
        return <FullScreenSpinner />;
    }

    // TODO this is a hotfix
    //   filter these out on the backend
    const fundableAccounts = apexAccounts?.filter((a) => a.canDepositFunds);

    return (
        <ScreenContainer
            onFallbackRoute="/home/investment"
            title="Fund account"
            hasBackButton={true}
        >
            <YStack gap={24}>
                {shouldWarnBackdoorRothIctDeposit(selectedApexAccount) && (
                    <BackdoorRothIctDepositWarningNotice />
                )}
                {shouldShowBackdoorTradConversionNotice(
                    selectedApexAccount,
                ) && (
                    <BackdoorTradIctDepositConversionNotice
                        traditionalIraAccount={selectedApexAccount}
                        apexAccounts={apexAccounts}
                    />
                )}
                <BodySmallRegular color="$gray1000">From</BodySmallRegular>
                <TransferAccountLineItem
                    placeholder="Select a bank account"
                    onPress={() => {
                        setIsSelectFromAccountSheetOpen(true);
                    }}
                    depositAccount={selectedDepositAccount}
                />
                <BodySmallRegular color="$gray1000">To</BodySmallRegular>
                <ApexAccountLineItem
                    placeholder="Select an investment account"
                    onPress={() => {
                        setIsSelectApexAccountSheetOpen(true);
                    }}
                    apexAccount={selectedApexAccount}
                />
                {isRetirementAccount && (
                    <>
                        <YStack>
                            <BodySmallRegular color="$gray1000">
                                Contribution type
                            </BodySmallRegular>
                            <SelectDropdown
                                disabled={
                                    selectedApexAccount == null ||
                                    !isRetirementAccount ||
                                    contributionTypes.length <= 1
                                }
                                defaultValue={contributionType}
                                options={contributionTypes}
                                onSelect={(selectedItem) => {
                                    setContributionYear(currentYearString);
                                    setContributionType(selectedItem);
                                }}
                            />
                        </YStack>
                        <YStack>
                            <BodySmallRegular color="$gray1000">
                                Contribution year
                            </BodySmallRegular>
                            <SelectDropdown
                                disabled={
                                    selectedApexAccount == null ||
                                    !isRetirementAccount ||
                                    contributionYears.length <= 1
                                }
                                defaultValue={contributionYear}
                                options={contributionYears}
                                onSelect={(selectedItem) => {
                                    setContributionYear(selectedItem);
                                }}
                            />
                        </YStack>
                    </>
                )}

                <Button
                    disabled={!isValid}
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={() => {
                        setApexDepositRequest(depositRequest);
                        router.navigate('/investments/transfer/deposit-amount');
                    }}
                />
                <FootnoteRegular color="$gray900">
                    Fruitful is a financial technology company, not a bank.
                    Deposit accounts provided by Emigrant Bank, Member FDIC.
                </FootnoteRegular>
                <FootnoteRegular color="$gray900">
                    {INVESTMENT_NOT_FDIC_INSURED}
                </FootnoteRegular>
            </YStack>
            {depositAccounts && (
                <>
                    <SelectAccountSheet
                        isOpen={isSelectFromAccountSheetOpen}
                        depositAccounts={depositAccounts}
                        onClose={() => {
                            setIsSelectFromAccountSheetOpen(false);
                        }}
                        onPlaidExit={() => {}}
                        onSelectAccount={(account) => {
                            setSelectedDepositAccount(account);
                        }}
                        enableLinkingNewAccount={false}
                        onNeedsReview={() => {}}
                    />
                </>
            )}
            {fundableAccounts && (
                <>
                    <SelectApexAccountSheet
                        isOpen={isSelectApexAccountSheetOpen}
                        apexAccounts={fundableAccounts}
                        onClose={() => {
                            setIsSelectApexAccountSheetOpen(false);
                        }}
                        onSelectAccount={(account) => {
                            const isSepIra =
                                account.registrationType ===
                                SupportedApexAccountRegistrationType.RegistrationIraSep;
                            const type = isSepIra
                                ? RetirementContributionType.EMPLOYER
                                : RetirementContributionType.REGULAR;
                            setContributionType(transformEnumToDisplay(type));
                            setContributionYear(currentYearString);
                            setSelectedApexAccount(account);
                        }}
                    />
                </>
            )}
        </ScreenContainer>
    );
};
